import { Image } from 'expo-image'
import * as ImageManipulator from 'expo-image-manipulator'
import * as ImagePicker from 'expo-image-picker'
import { Platform } from 'react-native'
import {
  doc,
  firestore,
  storage,
  ref,
  deleteObject,
  putFile,
  uploadBytes,
} from '~providers/firebase'
import { File } from 'expo-file-system'

export enum AvatarSource {
  Camera = 'camera',
  Gallery = 'gallery',
}

const getProfileURL = (userId: string | null | undefined): string | null => {
  if (!userId) {
    return null
  }

  return `https://firebasestorage.googleapis.com/v0/b/${
    storage.app.options.storageBucket
  }/o/${encodeURIComponent(`users/${userId}/thumbnail.jpg`)}?alt=media`
}

const captureAvatar = async (userId: string, source: AvatarSource): Promise<boolean> => {
  let pickerResult = null

  switch (source) {
    case AvatarSource.Camera: {
      const permissionResult = await ImagePicker.requestCameraPermissionsAsync()

      if (permissionResult.granted === false) {
        alert('Permission to access camera is required!')
        return false
      }

      pickerResult = await ImagePicker.launchCameraAsync({
        quality: 1,
        allowsEditing: true,
        aspect: [1, 1],
        exif: false,
      })
      break
    }
    case AvatarSource.Gallery: {
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync()

      if (permissionResult.granted === false) {
        alert('Permission to access camera roll is required!')
        return false
      }

      pickerResult = await ImagePicker.launchImageLibraryAsync({
        quality: 1,
        allowsEditing: true,
        aspect: [1, 1],
        exif: false,
      })

      break
    }
    default:
      throw new Error('Unhandled source')
  }

  if (pickerResult.canceled === true) {
    return false
  }

  const manipulated = await ImageManipulator.manipulateAsync(
    pickerResult.assets[0].uri,
    [
      {
        resize:
          pickerResult.assets[0].width > pickerResult.assets[0].height
            ? {
                width: 128,
              }
            : {
                height: 128,
              },
      },
    ],
    // Compress as a JPEG
    { compress: 0.5, format: ImageManipulator.SaveFormat.JPEG },
  )

  // Upload it
  try {
    const fileRef = ref(storage, `users/${userId}/thumbnail.jpg`)

    if (Platform.OS === 'web') {
      await fetch(manipulated.uri)
        .then((res) => res.blob())
        .then((blob) => uploadBytes(fileRef, blob))
    } else {
      await putFile(fileRef, manipulated.uri)
    }

    const url = getProfileURL(userId)
    if (url !== null) {
      await Image.prefetch(url)
      return true
    }
  } catch (error) {
    console.error('error uploading user thumbnail', error)
  }
  return false
}

const removeAvatar = async (userId: string): Promise<boolean> => {
  try {
    const fileRef = ref(storage, `users/${userId}/thumbnail.jpg`)
    await deleteObject(fileRef)

    return true
  } catch (error) {
    if ((error as any)?.code === 'storage/object-not-found') {
      // File doesn't exist, that's fine
      return true
    }
    console.error('error removing avatar', error)
  } finally {
    if (Platform.OS !== 'web') {
      try {
        const url = getProfileURL(userId)
        if (url !== null) {
          const path = await Image.getCachePathAsync(url)
          if (path !== null) {
            console.log('Clearing cached avatar', path)
            new File(path).delete()
          }
        }
        await Image.clearMemoryCache()
      } catch {
        // Ignore
      }
    }
  }
  return false
}

const getProfile = (userId: string) => doc(firestore, 'users', userId)

export default { getProfileURL, captureAvatar, removeAvatar, getProfile }
